/**
 * `CampaignMetricsPanel` — Task 157 performance-analytics surface
 * (reworked from the Task 107 observe-only panel).
 *
 * A self-contained panel that surfaces one paid campaign's performance over a
 * chosen date window. Dropped into the `CampaignDetail` of both
 * `AgencyPaidMediaPage` and `client/PaidMediaPage`. Two data sources, two
 * surfaces:
 *
 *   PRIMARY — aggregated KPIs + a daily time-series chart + a per-level
 *   breakdown, fed by the read-only `get-paid-media-performance-summary` EF.
 *   Aggregation happens SERVER-SIDE; the panel never sums rows or recomputes
 *   a ratio. Renders `PerformanceKpiCards`, `PerformanceTimeSeriesChart`, and
 *   `PerformanceBreakdown`.
 *
 *   SECONDARY — the raw day-by-day rows, demoted into a collapsible
 *   `<details>` ("تفاصيل يومية") so they are no longer the primary
 *   experience. Still fed by `listPaidMediaMetrics` with `metricLevel`
 *   OMITTED (one all-grains call), each row rendered as-is.
 *
 * Invariants preserved from Task 107:
 *   - No client-side aggregation in the panel — both EFs return finished
 *     numbers; the panel only buckets the raw rows by grain for display.
 *   - No internal identifiers on screen — UUID columns are routing /
 *     bucketing keys only, never display text.
 *   - Sensitive warehouse fields are not on either wire type, so they
 *     structurally cannot be rendered.
 *   - First page only for the raw table — a `next_cursor` surfaces a note,
 *     never a loop.
 *   - RLS (`paid_media.metrics.read`) is the load-bearing access gate; a
 *     cross-org / unassigned campaign collapses to 404 → the error state.
 */

import { useEffect, useMemo, useState } from 'react';
import { getPaidMediaPerformanceSummary, listPaidMediaMetrics } from '../../api/endpoints';
import type { AdSetRow, CampaignMetricRow, PaidMediaPerformanceSummaryData } from '../../api/types';
import { EmptyState, ErrorState, LoadingState } from '../states';
import { provisionalFromDate, rangeIncludesLast48h } from './freshness';
import { PerformanceBreakdown } from './PerformanceBreakdown';
import { PerformanceKpiCards } from './PerformanceKpiCards';
import { PerformanceTimeSeriesChart } from './PerformanceTimeSeriesChart';

const MAX_WINDOW_DAYS = 92;
const DEFAULT_WINDOW_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

interface CampaignMetricsPanelProps {
  readonly paidCampaignId: string;
  /** The campaign's already-loaded ad sets — used only to name-resolve
   *  ad-set / ad grain rows. Never used to widen the metrics query. */
  readonly adSets: ReadonlyArray<AdSetRow>;
}

type PanelState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly metrics: ReadonlyArray<CampaignMetricRow>;
      readonly nextCursor: string | null;
    }
  | { readonly status: 'error'; readonly error: unknown };

// The PRIMARY analytics surface is fed by the aggregation EF; its load is
// tracked separately from the raw daily table's `listPaidMediaMetrics` call.
type SummaryState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly summary: PaidMediaPerformanceSummaryData }
  | { readonly status: 'error'; readonly error: unknown };

// The summary has "no data" when the campaign spent nothing, was never seen,
// and has no daily series points in the window.
function summaryIsEmpty(summary: PaidMediaPerformanceSummaryData): boolean {
  return summary.kpis.spend === 0 && summary.kpis.impressions === 0 && summary.series.length === 0;
}

function toDateInputValue(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultWindow(): { readonly start: string; readonly end: string } {
  const now = new Date();
  return {
    start: toDateInputValue(new Date(now.getTime() - DEFAULT_WINDOW_DAYS * MS_PER_DAY)),
    end: toDateInputValue(now),
  };
}

// `<input type="date">` yields `YYYY-MM-DD`; the EF wants an ISO-8601
// timestamp. Start → start-of-day, end → end-of-day, both UTC.
function toStartIso(dateValue: string): string {
  return `${dateValue}T00:00:00.000Z`;
}
function toEndIso(dateValue: string): string {
  return `${dateValue}T23:59:59.999Z`;
}

// FE-side window guard — fast feedback only. The EF independently
// re-validates ISO format, end > start, and the 92-day cap.
function windowError(start: string, end: string): string | null {
  if (!start || !end) return 'اختر تاريخ بداية ونهاية.';
  const startMs = Date.parse(toStartIso(start));
  const endMs = Date.parse(toEndIso(end));
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return 'تاريخ غير صالح.';
  if (endMs <= startMs) return 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية.';
  if (endMs - startMs > MAX_WINDOW_DAYS * MS_PER_DAY) {
    return `النطاق الزمني يجب ألا يتجاوز ${MAX_WINDOW_DAYS} يوماً.`;
  }
  return null;
}

// Shared number formatters — exported so the analytics components
// (`PerformanceKpiCards` / `PerformanceBreakdown`) render money/count/ratio
// values identically to the raw daily table below.
export function fmtCount(n: number): string {
  return n.toLocaleString('en-US');
}
export function fmtMoney(n: number | null, currency: string | null): string {
  if (n === null) return '—';
  const num = n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return currency ? `${num} ${currency}` : num;
}
// CTR / CVR are fractions → percentage; ROAS is a multiplier → `x`. Display
// only — the wire keeps raw decimals. `null` denominators render as an em-dash.
export function fmtPercent(n: number | null): string {
  if (n === null) return '—';
  return `${(n * 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
}
export function fmtRoas(n: number | null): string {
  if (n === null) return '—';
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}x`;
}
function fmtWindow(windowStart: string, windowEnd: string): string {
  const s = new Date(windowStart).toLocaleDateString('ar-SA');
  const e = new Date(windowEnd).toLocaleDateString('ar-SA');
  return `${s} – ${e}`;
}

export function CampaignMetricsPanel({ paidCampaignId, adSets }: CampaignMetricsPanelProps) {
  const initialWindow = useMemo(() => defaultWindow(), []);
  const [startDate, setStartDate] = useState(initialWindow.start);
  const [endDate, setEndDate] = useState(initialWindow.end);
  const [reloadKey, setReloadKey] = useState(0);
  const [summaryState, setSummaryState] = useState<SummaryState>({ status: 'loading' });
  const [state, setState] = useState<PanelState>({ status: 'loading' });

  const rangeError = windowError(startDate, endDate);

  // Task 158 — Meta keeps reshaping the last ~48h; surface a freshness note
  // when the selected range touches it, and hand the chart the provisional
  // floor (a YYYY-MM-DD string, stable within a day) so it can shade the
  // most-recent days as provisional.
  const now = new Date();
  const provisionalFrom = provisionalFromDate(now);
  const showFreshnessNote = !rangeError && rangeIncludesLast48h(endDate, now);

  // PRIMARY load — the aggregated performance summary that feeds the KPI
  // cards, the time-series chart, and the breakdown. Server-side aggregation
  // (the panel does NOT sum rows itself).
  useEffect(() => {
    if (rangeError) return;
    const ctrl = new AbortController();
    setSummaryState({ status: 'loading' });
    getPaidMediaPerformanceSummary({
      paidCampaignId,
      windowStart: toStartIso(startDate),
      windowEnd: toEndIso(endDate),
      signal: ctrl.signal,
    })
      .then((summary) => {
        if (ctrl.signal.aborted) return;
        setSummaryState({ status: 'ready', summary });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setSummaryState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [paidCampaignId, startDate, endDate, reloadKey, rangeError]);

  // SECONDARY load — the raw daily rows for the collapsible "تفاصيل يومية"
  // section. Unchanged contract: `metricLevel` is intentionally OMITTED —
  // one call returns all three grains for this campaign; the panel buckets
  // them client-side and renders each row as-is (no summing).
  useEffect(() => {
    // An invalid window never reaches the EF — the guard message renders
    // instead. The EF stays the real validator for anything that slips by.
    if (rangeError) return;
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    listPaidMediaMetrics({
      paidCampaignId,
      windowStart: toStartIso(startDate),
      windowEnd: toEndIso(endDate),
      signal: ctrl.signal,
    })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setState({ status: 'ready', metrics: data.metrics, nextCursor: data.next_cursor });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [paidCampaignId, startDate, endDate, reloadKey, rangeError]);

  const adSetNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of adSets) map.set(s.id, s.name);
    return map;
  }, [adSets]);

  // Bucket the single all-grains response by `metric_level`. No summing —
  // each row is rendered as-is; we only group + sort by window.
  const buckets = useMemo(() => {
    const grouped = {
      campaign: [] as CampaignMetricRow[],
      ad_set: [] as CampaignMetricRow[],
      ad: [] as CampaignMetricRow[],
    };
    if (state.status !== 'ready') return grouped;
    for (const row of state.metrics) grouped[row.metric_level].push(row);
    for (const grain of ['campaign', 'ad_set', 'ad'] as const) {
      grouped[grain].sort((a, b) => b.window_start.localeCompare(a.window_start));
    }
    return grouped;
  }, [state]);

  const adSetLabel = (row: CampaignMetricRow): string => {
    if (!row.ad_set_id) return 'مجموعة إعلانية';
    return adSetNameById.get(row.ad_set_id) ?? 'مجموعة إعلانية';
  };

  return (
    <div className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>الأداء</h2>
        <span className="meta">بيانات الأداء</span>
      </div>

      <div className="toolbar-row" style={{ gap: 12, alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="lbl">من</span>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span className="lbl">إلى</span>
          <input
            type="date"
            value={endDate}
            min={startDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </label>
      </div>

      {showFreshnessNote ? (
        <p className="tiny" style={{ marginTop: 8, color: 'var(--muted)' }}>
          قد تتغير بيانات آخر 48 ساعة بحسب تحديثات منصة Meta.
        </p>
      ) : null}

      {rangeError ? (
        <p className="tiny" style={{ marginTop: 8, color: '#b91c1c' }}>
          {rangeError}
        </p>
      ) : null}

      {/* PRIMARY surface — aggregated KPI cards + chart + breakdown. */}
      {!rangeError && summaryState.status === 'loading' ? (
        <LoadingState title="جاري تحميل بيانات الأداء…" variant="inline" />
      ) : null}

      {!rangeError && summaryState.status === 'error' ? (
        <ErrorState
          title="تعذر تحميل بيانات الأداء"
          error={summaryState.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}

      {!rangeError && summaryState.status === 'ready' ? (
        summaryIsEmpty(summaryState.summary) ? (
          <EmptyState
            title="لا توجد بيانات أداء بعد"
            description="لم تتم مزامنة أي بيانات أداء لهذه الحملة من المنصة. ستظهر هنا تلقائياً بعد أول عملية مزامنة."
          />
        ) : (
          <div className="stack-md" style={{ marginTop: 12 }}>
            {summaryState.summary.partial ? (
              <p className="tiny" style={{ color: 'var(--muted)' }}>
                النتائج تمثل جزءاً من النطاق الزمني فقط؛ النطاقات الأطول قيد التحسين.
              </p>
            ) : null}
            <PerformanceKpiCards
              kpis={summaryState.summary.kpis}
              currencyCode={summaryState.summary.currency_code}
            />
            <PerformanceTimeSeriesChart
              series={summaryState.summary.series}
              provisionalFromDate={provisionalFrom}
            />
            <PerformanceBreakdown breakdown={summaryState.summary.breakdown} />
          </div>
        )
      ) : null}

      {/* SECONDARY surface — the raw daily rows, demoted into a collapsible
          section so they are never the primary experience. Still fed by
          `listPaidMediaMetrics` (metricLevel OMITTED). */}
      {!rangeError && state.status === 'ready' && state.metrics.length > 0 ? (
        <details style={{ marginTop: 16 }}>
          <summary className="lbl" style={{ cursor: 'pointer' }}>
            تفاصيل يومية
          </summary>
          <div className="stack-md" style={{ marginTop: 12 }}>
            <GrainTable title="أداء الحملة" rows={buckets.campaign} contextFor={() => 'الحملة'} />
            <GrainTable
              title="أداء المجموعات الإعلانية"
              rows={buckets.ad_set}
              contextFor={adSetLabel}
            />
            <GrainTable title="أداء الإعلانات" rows={buckets.ad} contextFor={adSetLabel} />
            {state.nextCursor !== null ? (
              <p className="tiny" style={{ color: 'var(--muted)' }}>
                تُعرض الصفحة الأولى فقط من بيانات الأداء؛ توجد صفوف إضافية ضمن هذا النطاق الزمني.
              </p>
            ) : null}
          </div>
        </details>
      ) : null}
    </div>
  );
}

function GrainTable({
  title,
  rows,
  contextFor,
}: {
  readonly title: string;
  readonly rows: ReadonlyArray<CampaignMetricRow>;
  readonly contextFor: (row: CampaignMetricRow) => string;
}) {
  if (rows.length === 0) return null;
  return (
    <div>
      <div className="sec-title" style={{ marginTop: 8 }}>
        <h2>{title}</h2>
        <span className="meta">{rows.length}</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>السياق</th>
              <th>النافذة</th>
              <th>الإنفاق</th>
              <th>الظهور</th>
              <th>الوصول</th>
              <th>النقرات</th>
              <th>نقرات الرابط</th>
              <th>التحويلات</th>
              <th>قيمة التحويل</th>
              <th>CTR</th>
              <th>CPC</th>
              <th>CPM</th>
              <th>CVR</th>
              <th>ROAS</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{contextFor(row)}</td>
                <td>{fmtWindow(row.window_start, row.window_end)}</td>
                <td className="num">{fmtMoney(row.spend, row.currency_code)}</td>
                <td className="num">{fmtCount(row.impressions)}</td>
                <td className="num">{fmtCount(row.reach)}</td>
                <td className="num">{fmtCount(row.clicks)}</td>
                <td className="num">{fmtCount(row.link_clicks)}</td>
                <td className="num">{fmtCount(row.conversions)}</td>
                <td className="num">{fmtMoney(row.conversion_value, row.currency_code)}</td>
                <td className="num">{fmtPercent(row.ctr)}</td>
                <td className="num">{fmtMoney(row.cpc, row.currency_code)}</td>
                <td className="num">{fmtMoney(row.cpm, row.currency_code)}</td>
                <td className="num">{fmtPercent(row.cvr)}</td>
                <td className="num">{fmtRoas(row.roas)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
